import useSWR from 'swr';

import { Pack } from '@/components/app/cards/PacksList/types';

import { useApi } from '../api';

export interface SearchResponse {
	pack: Pack;
	query_id: string;
	search_index: string;
	// Sent only when the service substituted unfiltered results for a filtered
	// search that matched nothing; an ordinary response omits it.
	message?: string;
}

export interface UseSearchResult {
	isLoading: boolean;
	pack?: Pack;
	query_id?: string;
	search_index?: string;
	// Optional so existing callers/mocks that predate the server message don't
	// need updating to satisfy this type.
	message?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the untyped `error` SWR already surfaces here
	error: any;
	usedFallback: boolean;
}

enum SearchFilterType {
	CATEGORY = 'category',
	DURATION = 'duration',
}
export interface Filter {
	id: SearchFilterType;
	multi_select: boolean;
	title: string;
	options: { id: string; title: string }[];
}
export interface SearchCategoriesResponse {
	filters: {
		id: SearchFilterType;
		multi_select: boolean;
		title: string;
		options: { id: string; title: string }[];
	}[];
}

export interface SearchTrendingResponse {
	terms: {
		term: string;
	}[];
}

export default function useSearch(query?: string, category?: string[], duration?: string[]): UseSearchResult {
	const apiRequest = useApi();
	const baseEndpoint = 'search/v2';

	const getSearchEndpoint = (query?: string, category?: string[], duration?: string[]) => {
		return `${baseEndpoint}?query=${query}&category=${category}&duration=${duration}`;
	};

	const fetchData = async (): Promise<SearchResponse> => {
		const response = await apiRequest<SearchResponse>({
			endpoint: getSearchEndpoint(query, category, duration),
			method: 'GET',
		});
		return response.data;
	};

	const { data, isLoading, error } = useSWR<SearchResponse>(
		[baseEndpoint, query, category, duration].filter(Boolean),
		fetchData,
		{ revalidateOnFocus: false, errorRetryCount: 0, keepPreviousData: true },
	);

	return {
		isLoading: isLoading,
		pack: data?.pack,
		query_id: data?.query_id,
		search_index: data?.search_index,
		message: data?.message,
		error,
		// The field's presence, not its truthiness, is the signal that the service
		// substituted results: it is optional in the response schema, so an empty
		// string is valid and still means a substitution happened — the banner then
		// falls back to web's own copy rather than disappearing. Reading it from the
		// same payload as `pack` keeps the two on one clock under `keepPreviousData`.
		usedFallback: data?.message !== undefined,
	};
}

export function useSearchCategories() {
	const apiRequest = useApi();
	const endpoint = 'search/v2/config';
	const { data, isLoading, error } = useSWR<SearchCategoriesResponse>(
		endpoint,
		async () => {
			const res = await apiRequest<SearchCategoriesResponse>({
				endpoint: endpoint,
				method: 'GET',
			});
			return res.data;
		},
		{ revalidateOnFocus: true, errorRetryCount: 0, keepPreviousData: true },
	);
	return { isLoading: isLoading, filters: data?.filters, error };
}

export function useSearchTrending() {
	const apiRequest = useApi();
	const baseEndpoint = 'search/trending';

	const { data, isLoading, error } = useSWR<SearchTrendingResponse>(
		baseEndpoint,
		async () => {
			const res = await apiRequest<SearchTrendingResponse>({
				endpoint: `${baseEndpoint}`,
				method: 'GET',
			});
			return res.data;
		},
		{ revalidateOnFocus: false, errorRetryCount: 0, keepPreviousData: true },
	);

	return { isLoading, terms: data?.terms, error };
}

export interface SearchRecsResponse {
	input: string;
	terms: { term: string }[];
}

export function useSearchRecs(input?: string, limit?: number) {
	const apiRequest = useApi();
	const baseEndpoint = 'search/recommend';
	const { data, error, isLoading } = useSWR<SearchRecsResponse>(
		input ? [baseEndpoint, input] : [baseEndpoint],

		async () => {
			const res = await apiRequest<SearchRecsResponse>({
				endpoint: baseEndpoint,
				method: 'POST',
				body: { query: input, limit },
			});
			return res.data;
		},
		{ revalidateOnFocus: false, errorRetryCount: 0, keepPreviousData: true },
	);

	return { searchRecs: data?.terms, error, isLoading };
}
